import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { copyContent, emptyContent, addContent as addContentToForm, deleteContent as deleteContentFromForm } from '../../../../Redux/Actions/formAction';
import { addContentInPlaylist } from '../../../../Redux/Actions/playlistAction';
import { imgUrl } from '../../../../api/api';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import LinkIcon from '@mui/icons-material/Link';
import AppsIcon from '@mui/icons-material/Apps';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import TimerIcon from '@mui/icons-material/Timer';
import './AddContentDialog.css';

const TYPE_TABS = [
  { key: 'all',   label: '전체',   icon: <AppsIcon fontSize="small" /> },
  { key: 'image', label: '이미지', icon: <ImageIcon fontSize="small" /> },
  { key: 'video', label: '비디오', icon: <VideocamIcon fontSize="small" /> },
  { key: 'url',   label: 'URL',    icon: <LinkIcon fontSize="small" /> },
];

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear().toString().slice(2)}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

// 파일명 길이에 따라 폰트 크기 조정
function nameFontSize(name) {
  if (!name) return '13px';
  if (name.length > 50) return '10px';
  if (name.length > 35) return '11px';
  return '13px';
}

function Thumbnail({ data }) {
  if (data.type === 'image') {
    return (
      <img
        src={`${imgUrl}/${data.url}`}
        alt={data.name}
        className="acd-thumb-img"
        onError={e => { e.target.style.display = 'none'; }}
      />
    );
  }
  if (data.type === 'video') {
    return (
      <video
        src={`${imgUrl}/${data.url}`}
        className="acd-thumb-img"
        muted
        preload="metadata"
      />
    );
  }
  return (
    <div className="acd-thumb-url">
      <LinkIcon sx={{ color: '#551FFF', fontSize: 22 }} />
    </div>
  );
}

function TypeBadge({ type }) {
  const map = {
    image: { label: '이미지', color: '#22c55e' },
    video: { label: '비디오', color: '#3b82f6' },
    url:   { label: 'URL',   color: '#f59e0b' },
  };
  const t = map[type] || { label: type, color: '#aaa' };
  return (
    <span className="acd-badge" style={{ background: t.color }}>
      {t.label}
    </span>
  );
}

function AddContentDialog({ pId, screenMac, socketRef, onClose }) {
  const dispatch = useDispatch();
  const { contentForm, isContentAvailable } = useSelector(s => s.Form);
  const { content: allContent } = useSelector(s => s.Content);
  const [activeType, setActiveType] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [bulkDuration, setBulkDuration] = useState(5); // 초 단위
  // mac → { w, h } 로 세로 여부 판별
  const [imgDimensions, setImgDimensions] = useState({});
  const loadedRef = useRef(new Set());

  useEffect(() => {
    if (isContentAvailable) dispatch(emptyContent());
  }, []);

  useEffect(() => {
    dispatch(copyContent([]));
  }, []);

  // 이미지 자연 크기를 비동기로 감지 → 세로 이미지 판별용
  useEffect(() => {
    if (!allContent) return;
    allContent.filter(i => i.type === 'image').forEach(item => {
      if (loadedRef.current.has(item.id)) return;
      loadedRef.current.add(item.id);
      const img = new Image();
      img.onload = () => {
        setImgDimensions(prev => ({
          ...prev,
          [item.id]: { w: img.naturalWidth, h: img.naturalHeight },
        }));
      };
      img.src = `${imgUrl}/${item.url}`;
    });
  }, [allContent]);

  const isPortrait = (item) => {
    if (item.type !== 'image') return false;
    const dims = imgDimensions[item.id];
    return dims ? dims.h > dims.w : false;
  };

  const filtered = (allContent || []).filter(item => {
    const matchType = activeType === 'all' || item.type === activeType;
    const matchSearch = !searchText ||
      item.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      (item.type === 'url' && item.url?.toLowerCase().includes(searchText.toLowerCase()));
    return matchType && matchSearch;
  });

  // 세로 파일 먼저 정렬
  const sorted = [...filtered].sort((a, b) => {
    const ap = isPortrait(a) ? 0 : 1;
    const bp = isPortrait(b) ? 0 : 1;
    return ap - bp;
  });

  const isSelected = (id) => !!(contentForm?.value || []).find(c => c.id === id);

  const toggleItem = (item) => {
    if (isSelected(item.id)) {
      dispatch(deleteContentFromForm(pId, item.id));
    } else {
      dispatch(addContentToForm(pId, item));
    }
  };

  const emitAddContent = (mac) => {
    const id = setTimeout(() => {
      socketRef?.current?.emit('content_added', { detail: { mac } });
      clearTimeout(id);
    }, 2000);
  };

  const handleSave = () => {
    // 이미지 항목에 bulkDuration 적용 (ms 단위)
    const valueWithDuration = (contentForm?.value || []).map(item => ({
      ...item,
      duration: item.type === 'image' ? bulkDuration * 1000 : item.duration,
    }));
    dispatch(addContentInPlaylist({ ...contentForm, value: valueWithDuration }));
    if (screenMac) emitAddContent(screenMac);
    onClose(false);
  };

  const selectedCount = contentForm?.id === pId ? (contentForm?.value || []).length : 0;
  const selectedImages = (contentForm?.value || []).filter(i => i.type === 'image');
  const showBulkDuration = selectedImages.length >= 2;

  return (
    <div className="acd-root">
      {/* 타입 필터 탭 */}
      <div className="acd-type-tabs">
        {TYPE_TABS.map(tab => (
          <button
            key={tab.key}
            className={`acd-type-btn${activeType === tab.key ? ' acd-type-btn--active' : ''}`}
            onClick={() => setActiveType(tab.key)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* 검색바 */}
      <div className="acd-search-wrap">
        <SearchIcon className="acd-search-icon" />
        <input
          className="acd-search-input"
          placeholder="파일명 또는 URL 검색..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
        {searchText && (
          <button className="acd-search-clear" onClick={() => setSearchText('')}>✕</button>
        )}
      </div>

      {/* 선택 카운트 + 일괄 재생시간 */}
      {selectedCount > 0 && (
        <div className="acd-selected-bar">
          <CheckIcon fontSize="small" />
          <span>{selectedCount}개 선택됨</span>

          {/* 이미지 2개 이상 선택 시 일괄 재생시간 조정 */}
          {showBulkDuration && (
            <div className="acd-bulk-duration">
              <TimerIcon style={{ fontSize: 15 }} />
              <span>이미지 재생시간</span>
              <input
                type="number"
                className="acd-duration-input"
                min={1}
                max={300}
                value={bulkDuration}
                onChange={e => setBulkDuration(Math.max(1, parseInt(e.target.value) || 1))}
              />
              <span>초</span>
              <span className="acd-duration-note">({selectedImages.length}개 이미지 일괄 적용)</span>
            </div>
          )}
        </div>
      )}

      {/* 콘텐츠 리스트 */}
      <div className="acd-list">
        {sorted.length === 0 ? (
          <div className="acd-empty">
            {searchText ? '검색 결과가 없습니다.' : '등록된 콘텐츠가 없습니다.'}
          </div>
        ) : (
          sorted.map(item => {
            const selected = isSelected(item.id);
            const portrait = isPortrait(item);
            return (
              <div
                key={item.id}
                className={`acd-row${selected ? ' acd-row--selected' : ''}`}
                onClick={() => toggleItem(item)}
              >
                {/* 썸네일 */}
                <div className={`acd-thumb${portrait ? ' acd-thumb--portrait' : ''}`}>
                  <Thumbnail data={item} />
                  {portrait && <span className="acd-portrait-badge">세로</span>}
                </div>

                {/* 정보 */}
                <div className="acd-info">
                  <p
                    className="acd-name"
                    title={item.name}
                    style={{ fontSize: nameFontSize(item.name) }}
                  >
                    {item.name}
                  </p>
                  {item.type === 'url' && (
                    <p className="acd-url" title={item.url}>{item.url}</p>
                  )}
                  <div className="acd-meta">
                    <TypeBadge type={item.type} />
                    {item.createdAt && (
                      <span className="acd-date">{formatDate(item.createdAt)}</span>
                    )}
                  </div>
                </div>

                {/* 추가 버튼 */}
                <button
                  className={`acd-add-btn${selected ? ' acd-add-btn--selected' : ''}`}
                  onClick={e => { e.stopPropagation(); toggleItem(item); }}
                >
                  {selected ? <CheckIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                  <span>{selected ? '선택됨' : '추가'}</span>
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* 푸터 버튼 */}
      <div className="acd-footer">
        <button className="acd-btn-cancel" onClick={() => onClose(false)}>취소</button>
        <button
          className="acd-btn-save"
          onClick={handleSave}
          disabled={selectedCount === 0}
        >
          저장 {selectedCount > 0 && `(${selectedCount}개)`}
        </button>
      </div>
    </div>
  );
}

export default AddContentDialog;
